// TodoForm — Create/edit form for CRM to-do items
import { useState } from 'react'
import { X } from 'lucide-react'
import { createCrmTodo, updateCrmTodo } from '../../services/api'
import type { CrmTodo, TodoPriority, TodoRecurrence, TodoScope } from '../../types'

interface TodoFormProps {
  entityTypeId: string
  todo?: CrmTodo
  onSave: (todo: CrmTodo) => void
  onCancel: () => void
}

export default function TodoForm({ entityTypeId, todo, onSave, onCancel }: TodoFormProps) {
  const [title, setTitle] = useState(todo?.title ?? '')
  const [description, setDescription] = useState(todo?.description ?? '')
  const [priority, setPriority] = useState<TodoPriority>(todo?.priority ?? 'medium')
  const [dueDate, setDueDate] = useState(todo?.due_date ?? '')
  const [recurrence, setRecurrence] = useState<TodoRecurrence>(todo?.recurrence ?? 'none')
  const [assigneeId, setAssigneeId] = useState(todo?.assignee_id ?? '')
  const [scope, setScope] = useState<TodoScope>(todo?.scope ?? 'document')
  const [tagsRaw, setTagsRaw] = useState(todo?.tags?.join(', ') ?? '')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setSaving(true)
    const tags = tagsRaw.split(',').map((t) => t.trim()).filter(Boolean)
    try {
      let result: CrmTodo
      if (todo) {
        result = await updateCrmTodo(entityTypeId, todo.todo_id, {
          title: title.trim(),
          description,
          priority,
          due_date: dueDate,
          recurrence,
          assignee_id: assigneeId,
          tags,
        })
      } else {
        result = await createCrmTodo({
          entity_type_id: entityTypeId,
          title: title.trim(),
          description,
          priority,
          due_date: dueDate,
          recurrence,
          scope,
          assignee_id: assigneeId,
          tags,
          status: 'open',
        })
      }
      onSave(result)
    } catch {
      alert('Failed to save to-do')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-900">{todo ? 'Edit To-Do' : 'Add To-Do'}</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="To-do title"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Optional details..."
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as TodoPriority)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Recurrence</label>
              <select
                value={recurrence}
                onChange={(e) => setRecurrence(e.target.value as TodoRecurrence)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="none">None</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Scope</label>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as TodoScope)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="document">Document</option>
                <option value="project">Project</option>
                <option value="ecosystem">Ecosystem</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Assignee ID</label>
              <input
                type="text"
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                placeholder="user-id"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tags <span className="text-gray-500 font-normal">(comma-separated)</span></label>
            <input
              type="text"
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="e.g. follow-up, sales"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : todo ? 'Save Changes' : 'Add To-Do'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
